import { useMemo } from 'react';

import { uniqBy } from 'ramda';
import { Apollo } from 'src/gql/generated';

export function useGetDocumentUser(
  documentId: UUID,
  userId?: UUID | null
): {
  loading: boolean;
  user: NonNullable<Apollo.GetDocumentUserQueryResult['data']>['documentUser'] | null;
} {
  const { data, loading } = Apollo.useGetDocumentUserQuery({
    skip: !userId,
    variables: { input: { documentId, userId: userId ?? '' } },
  });

  return { loading, user: data?.documentUser ?? null };
}

export function useGetMyRecentDocuments(
  options?: Parameters<typeof Apollo.useGetMyRecentDocumentsQuery>[0]
): {
  loading: boolean;
  myRecentDocuments: NonNullable<
    NonNullable<Apollo.GetMyRecentDocumentsQueryResult['data']>['currentUser']
  >['myRecentDocuments'];
} {
  const { data, loading } = Apollo.useGetMyRecentDocumentsQuery(options);

  return { loading, myRecentDocuments: data?.currentUser?.myRecentDocuments ?? [] };
}

export function useGetOrgMembers(options: Parameters<typeof Apollo.useGetMyOrgMembersQuery>[0]) {
  const { data, error, loading, refetch } = Apollo.useGetMyOrgMembersQuery(options);
  const orgMembersPlusUser = useMemo(() => {
    const orgMembers = data?.currentUser?.org?.members ?? [];
    return uniqBy(u => u.id, data?.currentUser ? [...orgMembers, data.currentUser] : []);
  }, [data?.currentUser]);
  const orgContacts = useMemo(() => {
    return (data?.currentUser?.org?.contacts ?? []).flatMap(contact => {
      return [
        {
          ...contact.user,
          displayName: contact.name?.trim() ? contact.name : contact.user.displayName,
        },
      ];
    });
  }, [data?.currentUser?.org?.contacts]);

  return {
    error,
    loading,
    org: data?.currentUser?.org ?? null,
    orgContacts,
    orgMembers: orgMembersPlusUser,
    refetch,
  };
}

export function useHasDSIntegration(): { hasDSIntegration: boolean; loading: boolean } {
  const { data, loading } = Apollo.useHasDocuSignAuthQuery();

  return { hasDSIntegration: !!data?.hasDocuSignAuth.hasDocuSignIntegration, loading };
}
